// ---------------------------------------------------------------------------
// Bell Operations Console — data service
// ---------------------------------------------------------------------------
// Thin fetch layer. Every call returns { data, source } where source is
// "api" or "mock", so the page can surface a "sample data" banner. When
// CONFIG.DEMO is true (or the live call throws) it transparently falls back to
// the synthesised week, keeping the console usable before the backend exists.
// ---------------------------------------------------------------------------
import Axios from "../../../../axios";
import {fetchSchoolListForDropdown} from "../../../../common-services";
import {bellConstants, bellLogApiConstants} from "../../../../constants/ApiConstant";
import {normalizeOverall, normalizeSchool} from "../utils/overallUtils";
import {CONFIG} from "../constants/BellStatusConstants";
import {DEMO_SCHOOLS} from "../data/bellStatusMockData";
import {searchRoster} from "../../Roster/services";
import {searchBell} from "../../Roster/Bells/services";
import {searchBellLogs} from "../../../../Services/BellLogService";
import {buildWeek, minutes, to24h, todayISO} from "../utils/bellStatusUtils";

function authHeaders() {
    const h = {"Content-Type": "application/json"};
    let token = null;
    try {
        token = localStorage.getItem("token");
    } catch (e) {
        token = null;
    }
    if (token) h.Authorization = "Bearer " + token;
    return h;
}

// The shared /school/active/min dropdown endpoint returns [{ value, label, … }].
// Normalise it into the shape the console works with ({ id, name, code … }),
// while passing through anything already in the console shape (mock data).
function normalizeSchools(list) {
    return (list || [])
        .map((s) => {
            if (s && (s.value !== undefined || s.label !== undefined)) {
                return {
                    id: String(s.value),
                    name: s.label || s.name || `School ${s.value}`,
                    name_ne: s.label_ne || s.name_ne || null,
                    code: s.code || s.schoolCode || "",
                    district: s.district || s.address || "",
                    students: s.students ?? s.studentCount ?? null,
                    type: s.type || s.category || "",
                    logo: s.logo || s.schoolLogo || s.logoUrl || s.image || null,
                };
            }
            return s;
        })
        .filter((s) => s && s.id != null);
}

// Real school dropdown. Always tries the live endpoint (independent of
// CONFIG.DEMO so the picker is real even while bell data is still sampled),
// falling back to the sample list only if the call fails or returns nothing.
export async function fetchSchools() {
    try {
        const res = await fetchSchoolListForDropdown();
        const norm = normalizeSchools(res && res.data);
        if (norm.length) return {data: norm, source: "api"};
        throw new Error("empty school list");
    } catch (e) {
        return {data: DEMO_SCHOOLS, source: "mock", error: e};
    }
}

// Pick the roster whose date range covers today, else the most recent one.
function pickRoster(rosters) {
    const today = todayISO();
    const dateOnly = (v) => (v ? String(v).slice(0, 10) : null);
    const covering = rosters.find((r) => {
        const start = dateOnly(r.startDate);
        const end = dateOnly(r.endDate);
        return (!start || start <= today) && (!end || end >= today);
    });
    if (covering) return covering;
    return [...rosters].sort((a, b) => String(b.startDate || "").localeCompare(String(a.startDate || "")))[0];
}

// Normalise a school's bells into the console's period rows, keeping only
// enabled bells and sorting them by scheduled time.
function normalizePeriods(bells) {
    return (Array.isArray(bells) ? bells : [])
        .filter((b) => (b.status ?? b.enabled) !== "N")
        .map((b) => {
            const raw = b.hourTime || b.time;
            const time = to24h(raw);
            const bellId = b.id ?? b.bellSetupId ?? null;
            return {
                id: String(bellId ?? raw),
                bellId,
                name: b.name || "Bell",
                time,
                rawTime: raw,
                strikes: 1,
                kind: b.type === "NATIONAL_ANTHEM" ? "assembly" : "period",
                type: b.type || null,
            };
        })
        .filter((p) => p.time)
        .sort((a, b) => minutes(a.time) - minutes(b.time));
}

// Fetch the selected school's periods by resolving its active roster and that
// roster's bells. Returns { periods, rosterId } (empty when none configured).
export async function fetchPeriods(schoolId) {
    const rosterRes = await searchRoster({schoolId: Number(schoolId), code: "", startDate: "", endDate: ""});
    const rosters = (rosterRes && rosterRes.data) || [];
    if (!rosters.length) return {periods: [], rosterId: null};

    const roster = pickRoster(rosters);
    const bellRes = await searchBell({id: roster.id, number: "", code: ""});
    const bells = (bellRes && bellRes.data) || [];
    return {periods: normalizePeriods(bells), rosterId: roster.id};
}

// Pull the school's bell-log events for the visible week (newest-first search,
// large page so the whole week fits), tolerating either a raw array or a
// Spring Page response.
async function fetchLogs(schoolId, from, to) {
    const res = await searchBellLogs({schoolId: Number(schoolId), from, to}, 0, 1000);
    const d = res && res.data;
    return Array.isArray(d) ? d : (d && (d.content || d.bellLogs || d.logs || d.items)) || [];
}

// The console's school context/drawer read a few identity fields off school.
function buildSchoolMeta(schoolMeta, schoolId) {
    const s = schoolMeta || {};
    return {
        ...s,
        id: schoolId,
        name: s.name || `School ${schoolId}`,
        code: s.code || "",
        address: s.address || (s.district ? `${s.district}, Nepal` : ""),
        zones: s.zones || [],
    };
}

// Real, mock-free week: real periods (school bells) coloured purely by the
// backend bell-log. Throws on failure so the page can show its error state.
export async function fetchWeek(schoolId, from, to, lang, schoolMeta) {
    const [{periods}, logs] = await Promise.all([
        fetchPeriods(schoolId),
        fetchLogs(schoolId, from, to),
    ]);
    const school = buildSchoolMeta(schoolMeta, schoolId);
    return {data: buildWeek(school, periods, logs, from, to), source: "api"};
}

// Super-admin overall view: today's bell status across all active schools, in
// one call. Returns { data: { schools, summary }, source }. `date` is an ISO
// day (defaults to today); the normaliser tolerates several payload shapes.
export async function fetchOverallBellStatus(date) {
    const res = await Axios.get(bellLogApiConstants.OVERALL_STATUS, {
        params: {date: date || todayISO()},
    });
    return {data: normalizeOverall(res && res.data), source: "api"};
}

// Today's bell queue for a specific school (superadmin passes schoolId). Used to
// hydrate a school's timeline in the overall drawer when the overall payload
// didn't ship per-bell rows. Returns a normalised school ({ bells, counts … }).
export async function fetchSchoolQueue(schoolId, schoolMeta) {
    const res = await Axios.get(bellConstants.BELL_QUEUE, {
        params: schoolId != null ? {schoolId: Number(schoolId)} : {},
    });
    const raw = (res && res.data) || [];
    const bells = Array.isArray(raw) ? raw : raw.bells || raw.queue || raw.content || [];
    return normalizeSchool({...(schoolMeta || {}), id: schoolId}, 0, bells);
}

export async function ringBell(schoolId, periodId, date) {
    if (CONFIG.DEMO) return {ok: true, queued: true};
    const r = await fetch(`${CONFIG.BASE}/api/v1/admin/schools/${schoolId}/ring`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({period_id: periodId, date}),
    });
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
}
